<template>
  <div class="fts-canvas-apply-history" v-if="enabled && history.length">
    <div class="apply-history-label">
      <font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="apply-history-icon" />
      Guarded changes <fts-release-badge alpha class="fts-align-top" />
    </div>
    <div
      v-for="record in history"
      :key="record.id"
      class="apply-history-row"
    >
      <span class="apply-history-time">{{ time(record) }}</span>
      <span class="apply-history-text">{{ record.label }}</span>
      <span class="apply-history-count">{{ opCount(record) }}</span>
      <fts-btn sm text color="primary" text-transform-init @click="revert(record)">
        Revert
      </fts-btn>
    </div>
    <fts-canvas-guard-dialog ref="ftsCanvasGuardDialog" />
  </div>
</template>

<script>
/* D12 apply-history strip — interim home near the console save/revision controls until
   the GNey panel exists (P2). Lists the engine's ledger for the open draft: guarded
   deletes now, AI proposal applies later. Revert composes the record's inverses into an
   ordinary proposal (checklist, conflicts unchecked) through the same engine.
   Alpha-gated (D14) via canvasOpsEnabled. */

import moment from "moment";
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import { describeOpHuman } from "../../business/canvasOps";
import FTSBtn from "../elements/FTSBtn";
import FTSReleaseBadge from "../elements/FTSReleaseBadge";
import FTSCanvasGuardDialog from "./FTSCanvasGuardDialog";

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-release-badge": FTSReleaseBadge,
    "fts-canvas-guard-dialog": FTSCanvasGuardDialog,
  },
  computed: {
    enabled() {
      return canvasDesignStore.getters.canvasOpsEnabled;
    },
    history() {
      return canvasDesignStore.getters.applyHistory.slice().reverse();
    },
  },
  methods: {
    time(record) {
      return moment(record.time).format("HH:mm");
    },
    opCount(record) {
      const n = record.ops.length + (record.authRows ? record.authRows.length : 0);
      return n === 1 ? "1 op" : `${n} ops`;
    },
    async revert(record) {
      const revert = await canvasDesignStore.dispatch("composeRevert", { record });
      const rows = revert.rows.map((row) => ({
        label: describeOpHuman(row.op, canvasDesignStore.state.canvas),
        reason: row.reason,
        conflict: row.conflict,
        op: row.op,
      }));
      const res = await this.$refs.ftsCanvasGuardDialog.show({
        mode: "revert",
        subject: record.label,
        revert: { rows },
      });
      if (!res) return;

      const result = await canvasDesignStore.dispatch("applyRevert", {
        record,
        ops: res.ops,
      });
      if (!result.ok) {
        const first = result.errors.concat(...result.ops.map((o) => o.errors))[0];
        this.$toasted.global.error({
          message: first ? first.message : "The revert could not be applied cleanly.",
        });
      } else {
        this.$toasted.info(`Reverted: ${record.label}`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.fts-canvas-apply-history {
  padding: 0.375rem 0.75rem;
  border-top: 1px solid var(--fts-gray-200);
  font-size: 0.8125rem;
  line-height: 1.5;

  .apply-history-label {
    font-weight: 500;
    color: var(--fts-gray-600);
    margin-bottom: 0.25rem;
    .apply-history-icon {
      font-size: 0.6875rem;
      margin-right: 0.25rem;
    }
  }
  .apply-history-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.125rem 0;
    .apply-history-time {
      color: var(--fts-gray-500);
      font-variant-numeric: tabular-nums;
    }
    .apply-history-text {
      flex: 1;
      word-break: break-word;
    }
    .apply-history-count {
      color: var(--fts-gray-500);
      white-space: nowrap;
    }
  }
}
</style>
